import { Info, AlertTriangle, Sparkles, CheckCircle2, Square } from "lucide-react";

/**
 * Tiny, dependency-free markdown renderer for training-module bodies.
 *
 * Supported syntax:
 *   # H1, ## H2, ### H3
 *   **bold**, *italic*, `code`
 *   - / * unordered list, 1. ordered list
 *   [ ] / [x] checklist (rendered with checkbox icon)
 *   > info callout, > !! warning, > ✨ tip (callout types via leading marker)
 *   ![alt](url)        inline image
 *   [text](url)        inline link
 *   ---                horizontal rule
 *   blank line         paragraph break
 *
 * The renderer trades flexibility for predictability — it knows exactly
 * what shape SEED_TRAINING uses and renders that beautifully. We do not
 * attempt to handle nested formatting, tables, or HTML.
 */
export default function TrainingMarkdown({ source }) {
  if (!source) return null;
  const blocks = parseBlocks(source);
  return (
    <div className="space-y-3.5 text-[15px] leading-relaxed text-slate-800" data-testid="training-md">
      {blocks.map((b, i) => (
        <Block key={i} b={b} />
      ))}
    </div>
  );
}

function Block({ b }) {
  if (b.type === "heading") {
    if (b.level === 1) {
      return <h2 className="font-display text-2xl font-extrabold text-slate-900 pt-2">{renderInline(b.text)}</h2>;
    }
    if (b.level === 2) {
      return <h3 className="font-display text-lg font-bold text-slate-900 pt-2 border-b border-slate-100 pb-1.5">{renderInline(b.text)}</h3>;
    }
    return <h4 className="text-[11px] uppercase tracking-[0.18em] font-bold text-blue-700 pt-1">{renderInline(b.text)}</h4>;
  }
  if (b.type === "ul") {
    return (
      <ul className="space-y-1.5 pl-4 list-disc marker:text-slate-400">
        {b.items.map((it, i) => (
          <li key={i} className="text-slate-800">{renderInline(it)}</li>
        ))}
      </ul>
    );
  }
  if (b.type === "ol") {
    return (
      <ol className="space-y-1.5 pl-5 list-decimal marker:text-slate-400 marker:font-semibold">
        {b.items.map((it, i) => (
          <li key={i} className="text-slate-800 pl-1">{renderInline(it)}</li>
        ))}
      </ol>
    );
  }
  if (b.type === "checklist") {
    return (
      <ul className="space-y-1.5">
        {b.items.map((it, i) => {
          const Icon = it.checked ? CheckCircle2 : Square;
          return (
            <li key={i} className="flex items-start gap-2 text-slate-800">
              <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${it.checked ? "text-emerald-600" : "text-slate-400"}`} />
              <span className={it.checked ? "text-slate-500 line-through" : ""}>{renderInline(it.text)}</span>
            </li>
          );
        })}
      </ul>
    );
  }
  if (b.type === "callout") {
    const tone = CALLOUT_TONES[b.tone] || CALLOUT_TONES.info;
    const Icon = tone.icon;
    return (
      <div className={`rounded-2xl border p-4 flex gap-2.5 ${tone.cls}`}>
        <Icon className="w-4 h-4 shrink-0 mt-0.5" />
        <div className="flex-1 text-[14px] leading-relaxed">{renderInline(b.text)}</div>
      </div>
    );
  }
  if (b.type === "image") {
    return (
      <figure className="rounded-2xl overflow-hidden border border-slate-200 bg-slate-50">
        <img src={b.url} alt={b.alt || ""} className="w-full h-auto" />
        {b.alt && <figcaption className="text-[11px] text-slate-500 px-3 py-2 border-t border-slate-100 bg-white">{b.alt}</figcaption>}
      </figure>
    );
  }
  if (b.type === "rule") {
    return <hr className="border-slate-200" />;
  }
  if (b.type === "code") {
    return (
      <pre className="rounded-xl bg-slate-900 text-emerald-200 text-[13px] font-mono p-3.5 overflow-x-auto leading-snug">
        <code>{b.text}</code>
      </pre>
    );
  }
  // Default: paragraph.
  return <p className="leading-relaxed">{renderInline(b.text)}</p>;
}

const CALLOUT_TONES = {
  info: { cls: "bg-blue-50 border-blue-100 text-blue-900", icon: Info },
  warn: { cls: "bg-amber-50 border-amber-200 text-amber-900", icon: AlertTriangle },
  tip: { cls: "bg-emerald-50 border-emerald-100 text-emerald-900", icon: Sparkles },
};

// ---------------- block parser ---------------------------------------------
function parseBlocks(src) {
  const lines = src.replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let i = 0;

  const flushPara = (buf) => {
    if (!buf.length) return;
    blocks.push({ type: "para", text: buf.join(" ").trim() });
  };

  let paraBuf = [];
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      flushPara(paraBuf); paraBuf = [];
      i++; continue;
    }

    // Horizontal rule.
    if (/^---+$/.test(trimmed)) {
      flushPara(paraBuf); paraBuf = [];
      blocks.push({ type: "rule" });
      i++; continue;
    }

    // Headings.
    const h = /^(#{1,3})\s+(.+)$/.exec(trimmed);
    if (h) {
      flushPara(paraBuf); paraBuf = [];
      blocks.push({ type: "heading", level: h[1].length, text: h[2].trim() });
      i++; continue;
    }

    // Image.
    const img = /^!\[(.*?)\]\((.+?)\)$/.exec(trimmed);
    if (img) {
      flushPara(paraBuf); paraBuf = [];
      blocks.push({ type: "image", alt: img[1], url: img[2] });
      i++; continue;
    }

    // Callout.
    const cm = /^>\s*(!!|✨|⚠️)?\s*(.+)$/.exec(trimmed);
    if (cm) {
      flushPara(paraBuf); paraBuf = [];
      const marker = cm[1] || "";
      const tone = marker === "!!" || marker === "⚠️" ? "warn" : marker === "✨" ? "tip" : "info";
      blocks.push({ type: "callout", tone, text: cm[2].trim() });
      i++; continue;
    }

    // Code block fenced ```.
    if (trimmed.startsWith("```")) {
      flushPara(paraBuf); paraBuf = [];
      const buf = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith("```")) {
        buf.push(lines[i]); i++;
      }
      i++; // skip closing fence
      blocks.push({ type: "code", text: buf.join("\n") });
      continue;
    }

    // Checklist.
    if (/^\[(\s|x|X)\]\s+/.test(trimmed)) {
      flushPara(paraBuf); paraBuf = [];
      const items = [];
      while (i < lines.length) {
        const t = lines[i].trim();
        const m = /^\[(\s|x|X)\]\s+(.+)$/.exec(t);
        if (!m) break;
        items.push({ checked: m[1].toLowerCase() === "x", text: m[2].trim() });
        i++;
      }
      blocks.push({ type: "checklist", items });
      continue;
    }

    // Ordered list.
    if (/^\d+[.)]\s+/.test(trimmed)) {
      flushPara(paraBuf); paraBuf = [];
      const items = [];
      while (i < lines.length) {
        const t = lines[i].trim();
        const m = /^\d+[.)]\s+(.+)$/.exec(t);
        if (!m) break;
        items.push(m[1].trim()); i++;
      }
      blocks.push({ type: "ol", items });
      continue;
    }

    // Unordered list.
    if (/^[-*•]\s+/.test(trimmed)) {
      flushPara(paraBuf); paraBuf = [];
      const items = [];
      while (i < lines.length) {
        const t = lines[i].trim();
        const m = /^[-*•]\s+(.+)$/.exec(t);
        if (!m) break;
        items.push(m[1].trim()); i++;
      }
      blocks.push({ type: "ul", items });
      continue;
    }

    // Paragraph buffer.
    paraBuf.push(trimmed);
    i++;
  }
  flushPara(paraBuf);
  return blocks;
}

// ---------------- inline renderer ------------------------------------------
function renderInline(text) {
  if (!text) return null;
  const parts = [];
  const re = /(\*\*([^*]+?)\*\*)|(\*([^*]+?)\*)|(`([^`]+?)`)|(\[([^\]]+)\]\(([^)]+?)\))/g;
  let last = 0;
  let key = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[1]) parts.push(<strong key={key++} className="font-semibold text-slate-900">{m[2]}</strong>);
    else if (m[3]) parts.push(<em key={key++}>{m[4]}</em>);
    else if (m[5]) parts.push(<code key={key++} className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-800 text-[13px] font-mono">{m[6]}</code>);
    else if (m[7]) parts.push(
      <a key={key++} href={m[9]} target="_blank" rel="noreferrer" className="text-blue-700 underline hover:text-blue-900">
        {m[8]}
      </a>
    );
    last = re.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}
